import { useState } from "react";
import { Plus, Loader2, ImagePlus, X, Package, Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATS = ["Packs", "Boots", "Clothing", "Optics", "Archery", "Firearms", "Calls & Decoys", "Camping", "Accessories", "Other"];
const EMPTY = { title: "", price: "", category: "Packs", species: "", state: "", description: "", spots_available: "", inventory: "", shipping_cost: "", shipping_days: "" };

export function ListingComposer({ onPublished, lockType }) {
  const [type, setType] = useState(lockType || "product");
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);

  const isProduct = type === "product";

  const uploadImages = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (images.length + files.length > 10) return toast.error("Up to 10 photos per product");
    setUploading(true);
    try {
      const r = await api.uploadListingImages(files);
      setImages((prev) => [...prev, ...(r.images || [])]);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));
  const uploadVariantImage = async (i, e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const r = await api.uploadListingImages([files[0]]);
      setVariants((p) => p.map((x, n) => n === i ? { ...x, image: r.images?.[0] } : x));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const [dragIdx, setDragIdx] = useState(null);
  const onDrop = (to) => {
    setImages((prev) => {
      if (dragIdx === null || dragIdx === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const buildBody = () => ({
    listing_type: type, title: form.title.trim(), price: Number(form.price),
    category: isProduct ? form.category : null, species: form.species || null, state: form.state || null,
    description: form.description || null, images,
    spots_available: form.spots_available ? Number(form.spots_available) : null,
    inventory: isProduct && form.inventory !== "" ? Number(form.inventory) : null,
    shipping_cost: isProduct && form.shipping_cost !== "" ? Number(form.shipping_cost) : null,
    shipping_days: isProduct ? (form.shipping_days || null) : null,
    variants: isProduct && variants.length
      ? variants.filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), stock: v.stock !== "" ? Number(v.stock) : null, price: v.price !== "" ? Number(v.price) : null, image: v.image || null }))
      : null,
  });

  const validate = () => {
    if (!form.title.trim() || !form.price) { toast.error("Title and price are required"); return false; }
    if (isProduct && images.length < 4) { toast.error("Products need at least 4 photos"); return false; }
    return true;
  };

  const resetForm = () => { setForm(EMPTY); setImages([]); setVariants([]); };

  const addToBatch = () => {
    if (!validate()) return;
    if (queue.length >= 30) return toast.error("You can batch up to 30 products at once");
    setQueue((prev) => [...prev, buildBody()]);
    resetForm();
    toast.success("Added to batch");
  };

  const publishSingle = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await api.createListing(buildBody());
      toast.success(isProduct ? "Product published" : "Listing published");
      resetForm();
      onPublished?.();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not publish"); }
    finally { setBusy(false); }
  };

  const publishBatch = async () => {
    const all = [...queue];
    if (form.title.trim() && form.price && images.length >= 4) all.push(buildBody());
    if (!all.length) return toast.error("Add at least one product to the batch");
    setBusy(true);
    try {
      const r = await api.createListingsBulk(all);
      toast.success(`Published ${r.published} product${r.published === 1 ? "" : "s"}`);
      setQueue([]); resetForm();
      onPublished?.();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not publish batch"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-saddle/20 p-5" data-testid="listing-composer">
      <h3 className="font-serif text-xl text-forest mb-1 flex items-center gap-2"><Plus size={18} /> New listing</h3>
      <p className="text-xs text-charcoal/50 mb-3">Products are unlimited and need at least 4 photos each. Batch up to 30 at once.</p>

      <div className="grid sm:grid-cols-2 gap-2">
        {lockType ? (
          <div className="border border-saddle/25 px-3 py-2 text-sm bg-bone/40 text-forest font-semibold capitalize" data-testid="listing-type-locked">{lockType === "hunt" ? "Guided Hunt" : lockType}</div>
        ) : (
          <select value={type} onChange={(e) => { setType(e.target.value); }} className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-type">
            <option value="product">Product</option>
            <option value="hunt">Guided Hunt</option>
            <option value="service">Service</option>
          </select>
        )}
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-title" />
        <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (USD)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-price" />
        {isProduct ? (
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-category">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
        ) : (
          <input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} placeholder="Species (optional)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-species" />
        )}
        <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State (optional)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-state" />
        {!isProduct && <input type="number" value={form.spots_available} onChange={(e) => setForm({ ...form, spots_available: e.target.value })} placeholder="Spots (optional)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-spots" />}
      </div>
      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none mt-2" data-testid="listing-desc" />

      {isProduct && (
        <div className="grid sm:grid-cols-3 gap-2 mt-2" data-testid="listing-inventory-shipping">
          <input type="number" min="0" value={form.inventory} onChange={(e) => setForm({ ...form, inventory: e.target.value })} placeholder="Stock qty (blank = ∞)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-inventory" />
          <input type="number" min="0" step="0.01" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} placeholder="Shipping $ (0 = free)" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-shipping-cost" />
          <input value={form.shipping_days} onChange={(e) => setForm({ ...form, shipping_days: e.target.value })} placeholder="Delivery e.g. 3–5 days" className="border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="listing-shipping-days" />
        </div>
      )}

      {isProduct && (
        <div className="mt-3" data-testid="listing-variants">
          <div className="flex items-center justify-between">
            <label className="hr-overline text-saddle">Options / variants <span className="text-charcoal/40 normal-case font-normal">(size, color…)</span></label>
            <button onClick={() => setVariants((p) => [...p, { label: "", stock: "", price: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold text-forest hover:text-blaze" data-testid="add-variant"><Plus size={13} /> Add option</button>
          </div>
          {variants.length > 0 && <p className="text-[11px] text-charcoal/45 mt-1">Each option tracks its own stock. Leave price blank to use the base price.</p>}
          <div className="space-y-1.5 mt-2">
            {variants.map((v, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`variant-row-${i}`}>
                <label className="w-9 h-9 shrink-0 border border-saddle/25 grid place-items-center cursor-pointer overflow-hidden bg-bone/40 hover:border-forest" title="Option photo" data-testid={`variant-photo-${i}`}>
                  {v.image ? <img src={v.image} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={14} className="text-saddle/60" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadVariantImage(i, e)} />
                </label>
                <input value={v.label} onChange={(e) => setVariants((p) => p.map((x, n) => n === i ? { ...x, label: e.target.value } : x))} placeholder='e.g. "Large / Forest Green"' className="flex-1 border border-saddle/25 px-2 py-1.5 text-sm outline-none" data-testid={`variant-label-${i}`} />
                <input type="number" min="0" value={v.stock} onChange={(e) => setVariants((p) => p.map((x, n) => n === i ? { ...x, stock: e.target.value } : x))} placeholder="Stock" className="w-20 border border-saddle/25 px-2 py-1.5 text-sm outline-none" data-testid={`variant-stock-${i}`} />
                <input type="number" min="0" step="0.01" value={v.price} onChange={(e) => setVariants((p) => p.map((x, n) => n === i ? { ...x, price: e.target.value } : x))} placeholder="$" className="w-20 border border-saddle/25 px-2 py-1.5 text-sm outline-none" data-testid={`variant-price-${i}`} />
                <button onClick={() => setVariants((p) => p.filter((_, n) => n !== i))} className="text-charcoal/40 hover:text-destructive px-1" aria-label="Remove option" data-testid={`variant-remove-${i}`}><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photos */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <label className="hr-overline text-saddle">Photos {isProduct && <span className={images.length >= 4 ? "text-[#2f7d4f]" : "text-blaze"}>({images.length}/4 min)</span>}</label>
          <label className="inline-flex items-center gap-1.5 bg-forest text-bone text-xs font-semibold px-3 py-1.5 cursor-pointer hover:bg-forest-2 transition-colors" data-testid="listing-photo-upload">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Add photos
            <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} disabled={uploading} data-testid="listing-photo-input" />
          </label>
        </div>
        {images.length > 0 && (
          <>
            <p className="text-[11px] text-charcoal/45 mt-1">Drag photos to reorder · the first photo is the cover shoppers see first.</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2" data-testid="listing-photo-grid">
              {images.map((src, i) => (
                <div
                  key={src}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  onDragEnd={() => setDragIdx(null)}
                  className={`relative aspect-square group cursor-move transition-opacity ${dragIdx === i ? "opacity-40" : ""}`}
                  data-testid={`listing-photo-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover border border-saddle/20 pointer-events-none" />
                  {i === 0 && <span className="absolute top-0.5 left-0.5 bg-forest text-bone text-[8px] font-bold px-1 py-0.5">COVER</span>}
                  <button onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove" data-testid={`listing-photo-remove-${i}`}><X size={11} /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button onClick={publishSingle} disabled={busy} className="bg-forest text-bone px-5 py-2.5 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="publish-listing">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Package size={15} />} Publish now
        </button>
        {isProduct && (
          <button onClick={addToBatch} disabled={busy} className="border border-forest text-forest px-5 py-2.5 text-sm font-semibold hover:bg-bone transition-colors disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="add-to-batch">
            <Plus size={15} /> Add to batch
          </button>
        )}
      </div>

      {/* Batch queue */}
      {queue.length > 0 && (
        <div className="mt-4 border-t border-saddle/15 pt-4" data-testid="batch-queue">
          <div className="flex items-center justify-between mb-2">
            <span className="hr-overline text-saddle flex items-center gap-1.5"><Layers size={13} /> Batch · {queue.length}/30 product(s)</span>
            <button onClick={publishBatch} disabled={busy} className="bg-blaze text-white px-4 py-2 text-sm font-semibold hover:bg-blaze-dark transition-colors disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="publish-batch">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />} Publish all ({queue.length + (form.title.trim() && images.length >= 4 ? 1 : 0)})
            </button>
          </div>
          <div className="space-y-1.5">
            {queue.map((q, i) => (
              <div key={i} className="flex items-center gap-2 bg-bone/50 px-3 py-2" data-testid={`batch-item-${i}`}>
                <img src={q.images[0]} alt="" className="w-8 h-8 object-cover shrink-0" />
                <span className="flex-1 min-w-0 text-sm text-forest truncate">{q.title}</span>
                <span className="text-xs text-charcoal/50">${q.price} · {q.images.length} photos</span>
                <button onClick={() => setQueue((prev) => prev.filter((_, n) => n !== i))} className="text-charcoal/40 hover:text-destructive" aria-label="Remove" data-testid={`batch-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
